
import { AuditQueryResult, CrudEntity, CrudEntityConstructionOptions, CrudRevisionEntity, fieldModified, RevisionType, UsernameRevisionInfo } from '@scisys-uim/uim-common';
import { ASSET_METADATA } from './asset.metadata';
import { AssetBase } from './asset.model.base';
import { ElectricalAspect } from '../../aspects/model/electrical-aspect.model';
import { MechanicalAspect } from '../../aspects/model/mechanical-aspect.model';
import { MaintenanceAspect } from '../../aspects/model/maintenance-aspect.model';
import { AssetType } from '../../assettype/model/asset-type.model';

export type ASSET_ASPECT = 'MechanicalAspect' | 'ElectricalAspect' | 'MaintenanceAspect';

export const ASSET_STATUS_ACTIVE = 'A';
export const ASSET_STATUS_DISPOSED = 'D';

export interface Asset extends AssetBase {
}

export interface AssetTransient { }

export class Asset extends CrudEntity<Asset, number, AssetTransient> implements AssetTransient {
  constructor(source?: Partial<Asset>, options?: CrudEntityConstructionOptions<Asset, number, AssetTransient>) {
    super(ASSET_METADATA, source, options);
  }

  initialiseEntityDefaults() {
    super.initialiseEntityDefaults();
    this.status = this.status || ASSET_STATUS_ACTIVE;
  }

  registerPropertyCalculators() {
    super.registerPropertyCalculators();
  }

  initAspectsForAssetType(createMode: boolean, assetType: AssetType = this.assetType) {
    //for each asset type, check if the aspect is required and if it is not present, create it
    if (assetType?.maintenanceAspectRequirement == 'REQUIRED') {
      if (!this.hasMaintenanceAspect) {
        this.createAspect('MaintenanceAspect');
      }
    } else {
      if (createMode) {
        this.removeAspect('MaintenanceAspect');
      }
    }
    if (assetType?.mechanicalAspectRequirement == 'REQUIRED') {
      if (!this.hasMechanicalAspect) {
        this.createAspect('MechanicalAspect');
      }
    } else {
      if (createMode) {
        this.removeAspect('MechanicalAspect');
      }
    }
    if (assetType?.electricalAspectRequirement == 'REQUIRED') {
      if (!this.hasElectricalAspect) {
        this.createAspect('ElectricalAspect');
      }
    } else {
      if (createMode) {
        this.removeAspect('ElectricalAspect');
      }
    }
    //if in create mode, call this again in case asset type has changed
    if (createMode) {
      this.setDefaultsForAspects();
    }
  }

  setDefaultsForAspects(aspect?: ASSET_ASPECT, assetType: AssetType = this.assetType) {
    if (aspect == 'MaintenanceAspect' || (aspect == null && this.hasMaintenanceAspect)) {
      this.maintenanceAspect.setDefaultsForAssetType(assetType);
    }
    if (aspect == 'MechanicalAspect' || (aspect == null && this.hasMechanicalAspect)) {
      this.mechanicalAspect.setDefaultsForAssetType(assetType);
    }
    if (aspect == 'ElectricalAspect' || (aspect == null && this.hasElectricalAspect)) {
      this.electricalAspect.setDefaultsForAssetType(assetType);
    }
  }

  createAspect(aspectName: ASSET_ASPECT, setDefaultsIfOptional = true) {
    if (aspectName == 'ElectricalAspect') {
      this.electricalAspect = new ElectricalAspect();
      if ((setDefaultsIfOptional && this.assetType?.electricalAspectRequirement == 'OPTIONAL') || this.assetType?.electricalAspectRequirement == 'REQUIRED') {
        this.setDefaultsForAspects('ElectricalAspect');
      }
    }
    if (aspectName == 'MechanicalAspect') {
      this.mechanicalAspect = new MechanicalAspect();
      if ((setDefaultsIfOptional && this.assetType?.mechanicalAspectRequirement == 'OPTIONAL') || this.assetType?.mechanicalAspectRequirement == 'REQUIRED') {
        this.setDefaultsForAspects('MechanicalAspect');
      }
    }
    if (aspectName == 'MaintenanceAspect') {
      this.maintenanceAspect = new MaintenanceAspect();
      if ((setDefaultsIfOptional && this.assetType?.maintenanceAspectRequirement == 'OPTIONAL') || this.assetType?.maintenanceAspectRequirement == 'REQUIRED') {
        this.setDefaultsForAspects('MaintenanceAspect');
      }
    }
  }

  removeAspect(aspectName: ASSET_ASPECT) {
    if (aspectName == 'ElectricalAspect') {
      this.electricalAspect = null;
    }
    if (aspectName == 'MechanicalAspect') {
      this.mechanicalAspect = null;
    }
    if (aspectName == 'MaintenanceAspect') {
      this.maintenanceAspect = null;
    }
  }

  hasAspect(aspectName: ASSET_ASPECT) {
    if (aspectName == 'ElectricalAspect') {
      return this.electricalAspect !== null;
    }
    if (aspectName == 'MechanicalAspect') {
      return this.mechanicalAspect !== null;
    }
    if (aspectName == 'MaintenanceAspect') {
      return this.maintenanceAspect !== null;
    }
  }

  get hasMaintenanceAspect() {
    return this.hasAspect('MaintenanceAspect');
  }

  get hasMechanicalAspect() {
    return this.hasAspect('MechanicalAspect');
  }

  get hasElectricalAspect() {
    return this.hasAspect('ElectricalAspect');
  }
}

export class AssetRevision extends Asset implements CrudRevisionEntity {

  _revisionInfo: UsernameRevisionInfo = null;
  _revisionChangedFields: string[] = [];
  _revisionType: RevisionType = null;

  constructor(auditQueryResult: AuditQueryResult<Asset>) {
    super(auditQueryResult.entity, { readOnly: true });
    this._revisionInfo = auditQueryResult.revision;
    this._revisionChangedFields = auditQueryResult.changes;
    this._revisionType = auditQueryResult.type;
  }

  fieldModified(field: string): boolean {
    return fieldModified(this, field);
  }

}